<template>
  <div class="revenue-analysis-container">
    <!-- 查询条件 -->
    <el-card class="query-card" shadow="hover">
      <div slot="header" class="card-header">
        <i class="el-icon-search"></i>
        <span>数据分析查询</span>
      </div>
      <el-form :inline="true" :model="queryParams" size="small">
        <el-form-item label="广告主">
          <el-select
            v-model="queryParams.advertiserId"
            placeholder="请选择广告主（可选）"
            filterable
            clearable
            style="width: 240px"
          >
            <el-option
              v-for="item in advertiserList"
              :key="item.advertiser_id"
              :label="`${item.advertiser_name.replace('-SUNNY-SINO-TT', '')} (${item.advertiser_id})`"
              :value="item.advertiser_id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="开始日期">
          <el-date-picker
            v-model="queryParams.startTime"
            type="date"
            placeholder="选择开始日期"
            value-format="yyyy-MM-dd"
            format="yyyy-MM-dd"
            style="width: 160px"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="结束日期">
          <el-date-picker
            v-model="queryParams.endTime"
            type="date"
            placeholder="选择结束日期"
            value-format="yyyy-MM-dd"
            format="yyyy-MM-dd"
            style="width: 160px"
          ></el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleQuery">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Tab 切换 -->
    <el-tabs v-model="activeTab" type="border-card" @tab-click="handleTabClick">
      <!-- Tab 1: 回收效率分析 -->
      <el-tab-pane label="回收效率分析" name="efficiency">
        <div v-if="efficiencyData" class="analysis-content">
          <!-- 汇总卡片 -->
          <el-row :gutter="20" class="summary-cards">
            <el-col :span="6">
              <el-card shadow="hover" class="summary-card">
                <div class="card-icon" style="background: #409EFF">
                  <i class="el-icon-wallet"></i>
                </div>
                <div class="card-info">
                  <div class="card-label">总消耗</div>
                  <div class="card-value">{{ formatMoney(efficiencyData.summary.total_spend) }}</div>
                </div>
              </el-card>
            </el-col>
            <el-col :span="6">
              <el-card shadow="hover" class="summary-card">
                <div class="card-icon" style="background: #67C23A">
                  <i class="el-icon-coin"></i>
                </div>
                <div class="card-info">
                  <div class="card-label">总收入</div>
                  <div class="card-value">{{ formatMoney(efficiencyData.summary.total_revenue) }}</div>
                </div>
              </el-card>
            </el-col>
            <el-col :span="6">
              <el-card shadow="hover" class="summary-card">
                <div class="card-icon" style="background: #E6A23C">
                  <i class="el-icon-data-line"></i>
                </div>
                <div class="card-info">
                  <div class="card-label">整体 ROI</div>
                  <div class="card-value" :class="getRoiClass(efficiencyData.summary.overall_roi)">
                    {{ (efficiencyData.summary.overall_roi || 0).toFixed(2) }}
                  </div>
                </div>
              </el-card>
            </el-col>
            <el-col :span="6">
              <el-card shadow="hover" class="summary-card">
                <div class="card-icon" style="background: #909399">
                  <i class="el-icon-s-grid"></i>
                </div>
                <div class="card-info">
                  <div class="card-label">广告组数量</div>
                  <div class="card-value">{{ efficiencyData.summary.adgroup_count }}</div>
                </div>
              </el-card>
            </el-col>
          </el-row>

          <!-- 详细列表 -->
          <el-table 
            :data="efficiencyData.list" 
            v-loading="loading"
            highlight-current-row
            style="margin-top: 20px"
          >
            <el-table-column prop="date" label="日期" width="120" align="center"></el-table-column>
            <el-table-column prop="adgroup_name" label="广告组名称" min-width="200" show-overflow-tooltip></el-table-column>
            <el-table-column prop="total_spend" label="总消耗" width="120" align="right">
              <template slot-scope="scope">
                <span class="amount">{{ formatMoney(scope.row.total_spend) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="latest_revenue" label="最新收入" width="120" align="right">
              <template slot-scope="scope">
                <span class="amount success">{{ formatMoney(scope.row.latest_revenue) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="roi" label="ROI" width="100" align="right">
              <template slot-scope="scope">
                <span :class="getRoiClass(scope.row.roi)">
                  {{ (scope.row.roi || 0).toFixed(2) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="max_hour_tracked" label="追踪进度" width="120" align="center">
              <template slot-scope="scope">
                <el-progress 
                  :percentage="(scope.row.max_hour_tracked / 24 * 100).toFixed(0)" 
                  :color="getProgressColor(scope.row.max_hour_tracked)"
                ></el-progress>
              </template>
            </el-table-column>
          </el-table>
          
          <!-- 分页 -->
          <el-pagination
            @size-change="handleEfficiencySizeChange"
            @current-change="handleEfficiencyCurrentChange"
            :current-page="efficiencyPagination.page"
            :page-sizes="[20,50, 100, 200, 500]"
            :page-size="efficiencyPagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="efficiencyPagination.total"
            style="margin-top: 20px; text-align: right"
          ></el-pagination>
        </div>
        <el-empty v-else description="请点击查询按钮获取数据"></el-empty>
      </el-tab-pane>

      <!-- Tab 2: Top ROI 排名 -->
      <el-tab-pane label="Top ROI 排名" name="topRoas">
        <div v-if="topRoasData" class="analysis-content">
          <el-alert
            :title="`共 ${topRoasData.total_count} 个广告组，显示前 ${topRoasData.top_n} 名`"
            type="success"
            :closable="false"
            style="margin-bottom: 20px"
          ></el-alert>

          <el-table 
            :data="topRoasData.top_adgroups" 
            v-loading="loading"
            highlight-current-row
          >
            <el-table-column type="index" label="排名" width="80" align="center">
              <template slot-scope="scope">
                <el-tag 
                  :type="scope.$index < 3 ? 'danger' : 'info'" 
                  size="medium"
                >
                  {{ scope.$index + 1 }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="adgroup_name" label="广告组名称" min-width="200" show-overflow-tooltip></el-table-column>
            <el-table-column prop="total_spend" label="总消耗" width="120" align="right">
              <template slot-scope="scope">
                <span class="amount">{{ formatMoney(scope.row.total_spend) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="total_revenue" label="总收入" width="120" align="right">
              <template slot-scope="scope">
                <span class="amount success">{{ formatMoney(scope.row.total_revenue) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="roi" label="ROI" width="100" align="right">
              <template slot-scope="scope">
                <span :class="getRoiClass(scope.row.roi)" style="font-weight: bold; font-size: 14px">
                  {{ (scope.row.roi || 0).toFixed(2) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="max_tracked_hour" label="追踪小时" width="100" align="center">
              <template slot-scope="scope">
                {{ scope.row.max_tracked_hour }}/24
              </template>
            </el-table-column>
          </el-table>
        </div>
        <el-empty v-else description="请点击查询按钮获取数据"></el-empty>
      </el-tab-pane>

      <!-- Tab 3: 投放时段热力图 -->
      <el-tab-pane label="投放时段热力图" name="heatmap">
        <div v-if="heatmapData" class="analysis-content">
          <!-- 汇总信息 -->
          <el-descriptions :column="4" border class="heatmap-summary">
            <el-descriptions-item label="总消耗">
              {{ formatMoney(heatmapData.summary.total_spend) }}
            </el-descriptions-item>
            <el-descriptions-item label="总收入">
              {{ formatMoney(heatmapData.summary.total_revenue) }}
            </el-descriptions-item>
            <el-descriptions-item label="整体 ROI">
              <span :class="getRoiClass(heatmapData.summary.overall_roi)">
                {{ (heatmapData.summary.overall_roi || 0).toFixed(2) }}
              </span>
            </el-descriptions-item>
            <el-descriptions-item label="数据点数量">
              {{ heatmapData.heatmap_data.length }}
            </el-descriptions-item>
          </el-descriptions>

          <!-- 最佳/最差时段 -->
          <el-row :gutter="20" style="margin-top: 20px">
            <el-col :span="12">
              <el-card shadow="hover" class="insight-card best">
                <div slot="header">
                  <i class="el-icon-trophy" style="color: #67C23A"></i>
                  <span>最佳投放时段</span>
                </div>
                <div v-if="heatmapData.summary.best_time_slot">
                  <div class="insight-time">{{ heatmapData.summary.best_time_slot.time_label }}</div>
                  <div class="insight-roi" :class="getRoiClass(heatmapData.summary.best_time_slot.roi)">
                    ROI: {{ (heatmapData.summary.best_time_slot.roi || 0).toFixed(2) }}
                  </div>
                  <div class="insight-detail">
                    消耗: {{ formatMoney(heatmapData.summary.best_time_slot.total_spend) }} | 
                    收入: {{ formatMoney(heatmapData.summary.best_time_slot.total_revenue) }}
                  </div>
                </div>
              </el-card>
            </el-col>
            <el-col :span="12">
              <el-card shadow="hover" class="insight-card worst">
                <div slot="header">
                  <i class="el-icon-warning" style="color: #F56C6C"></i>
                  <span>最差投放时段</span>
                </div>
                <div v-if="heatmapData.summary.worst_time_slot">
                  <div class="insight-time">{{ heatmapData.summary.worst_time_slot.time_label }}</div>
                  <div class="insight-roi" :class="getRoiClass(heatmapData.summary.worst_time_slot.roi)">
                    ROI: {{ (heatmapData.summary.worst_time_slot.roi || 0).toFixed(2) }}
                  </div>
                  <div class="insight-detail">
                    消耗: {{ formatMoney(heatmapData.summary.worst_time_slot.total_spend) }} | 
                    收入: {{ formatMoney(heatmapData.summary.worst_time_slot.total_revenue) }}
                  </div>
                </div>
              </el-card>
            </el-col>
          </el-row>

          <!-- 热力图 -->
          <div ref="heatmapChart" style="height: 600px; margin-top: 20px"></div>
        </div>
        <el-empty v-else description="请点击查询按钮获取数据"></el-empty>
      </el-tab-pane>

      <!-- Tab 4: 24小时趋势图 -->
      <el-tab-pane label="24小时趋势" name="trend">
        <el-form :inline="true" size="small" style="margin-bottom: 20px">
          <el-form-item label="广告组ID">
            <el-input 
              v-model="trendQuery.adgroupId" 
              placeholder="请输入广告组ID"
              style="width: 200px"
            ></el-input>
          </el-form-item>
          <el-form-item label="日期">
            <el-date-picker
              v-model="trendQuery.date"
              type="date"
              placeholder="选择日期"
              value-format="yyyy-MM-dd"
              format="yyyy-MM-dd"
              style="width: 160px"
            ></el-date-picker>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleTrendQuery">查询</el-button>
          </el-form-item>
        </el-form>

        <div v-if="trendData" class="analysis-content">
          <!-- 汇总卡片 -->
          <el-row :gutter="20" class="summary-cards">
            <el-col :span="8">
              <el-card shadow="hover" class="summary-card">
                <div class="card-info">
                  <div class="card-label">广告组</div>
                  <div class="card-value" style="font-size: 16px">{{ trendData.adgroup_name }}</div>
                </div>
              </el-card>
            </el-col>
            <el-col :span="8">
              <el-card shadow="hover" class="summary-card">
                <div class="card-info">
                  <div class="card-label">总消耗</div>
                  <div class="card-value">{{ formatMoney(trendData.summary.total_spend) }}</div>
                </div>
              </el-card>
            </el-col>
            <el-col :span="8">
              <el-card shadow="hover" class="summary-card">
                <div class="card-info">
                  <div class="card-label">整体 ROI</div>
                  <div class="card-value" :class="getRoiClass(trendData.summary.overall_roi)">
                    {{ (trendData.summary.overall_roi || 0).toFixed(2) }}
                  </div>
                </div>
              </el-card>
            </el-col>
          </el-row>

          <!-- 趋势图 -->
          <div ref="trendChart" style="height: 500px; margin-top: 20px"></div>
        </div>
        <el-empty v-else description="请输入广告组ID和日期后查询"></el-empty>
      </el-tab-pane>

      <!-- Tab 5: 24小时收入矩阵 -->
      <el-tab-pane label="24小时收入矩阵" name="matrix">
        <el-form :inline="true" size="small" style="margin-bottom: 20px">
          <el-form-item label="日期">
            <el-date-picker
              v-model="matrixQuery.date"
              type="date"
              placeholder="选择日期"
              value-format="yyyy-MM-dd"
              format="yyyy-MM-dd"
              style="width: 160px"
            ></el-date-picker>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleMatrixQuery">查询</el-button>
          </el-form-item>
        </el-form>

        <div v-if="matrixData" class="analysis-content">
<!--          <el-alert-->
<!--            :title="`日期: ${matrixData.date} | 共 ${matrixData.adgroup_count} 个广告组`"-->
<!--            type="info"-->
<!--            :closable="false"-->
<!--            style="margin-bottom: 20px"-->
<!--          ></el-alert>-->

          <el-table 
            :data="matrixData.list" 
            v-loading="loading"
            highlight-current-row
            border
            style="width: 100%"
          >
            <!-- 广告组信息列 -->
            <el-table-column prop="adgroup_name" label="广告组名称" style="font-family: 微软雅黑, serif" width="200" fixed show-overflow-tooltip></el-table-column>
            <el-table-column prop="total_spend" label="总消耗" width="100" align="right">
              <template slot-scope="scope">
                <span class="amount">{{ formatMoney(scope.row.total_spend) }}</span>
              </template>
            </el-table-column>
            
            <!-- 24小时列 -->
            <el-table-column 
              v-for="h in 24" 
              :key="h" 
              :label="(h-1) + ':00'" 
              width="100" 
              align="center"
            >
              <template slot-scope="scope">
                <div class="hour-cell">
                  <span class="hour-spend">{{ scope.row['hour_' + (h-1)].spend === 0 ? '-' : formatMoney(scope.row['hour_' + (h-1)].spend) }}</span>
                  <span class="hour-roas" :class="getRoiClass(scope.row['hour_' + (h-1)].roas)">
                    {{ (scope.row['hour_' + (h-1)].roas || 0) === 0 ? '-' : (scope.row['hour_' + (h-1)].roas || 0).toFixed(2) }}
                  </span>
                </div>
              </template>
            </el-table-column>
          </el-table>
          
          <!-- 分页 -->
          <el-pagination
            @size-change="handleMatrixSizeChange"
            @current-change="handleMatrixCurrentChange"
            :current-page="matrixPagination.page"
            :page-sizes="[20,50, 100, 200, 500]"
            :page-size="matrixPagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="matrixPagination.total"
            style="margin-top: 20px; text-align: right"
          ></el-pagination>
        </div>
        <el-empty v-else description="请选择日期后查询"></el-empty>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { advertiserApi } from '@/api/advertiserApi';

export default {
  name: 'RevenueAnalysis',
  data() {
    return {
      loading: false,
      activeTab: 'efficiency',
      queryParams: {
        advertiserId: '',
        startTime: '',
        endTime: ''
      },
      advertiserList: [],
      
      // 回收效率分析数据
      efficiencyData: null,
      efficiencyPagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },
      
      // Top ROI 数据
      topRoasData: null,
      
      // 热力图数据
      heatmapData: null,
      heatmapChart: null,
      
      // 24小时趋势查询
      trendQuery: {
        adgroupId: '',
        date: ''
      },
      trendData: null,
      trendChart: null,
      
      // 24小时收入矩阵查询
      matrixQuery: {
        date: ''
      },
      matrixData: null,
      matrixPagination: {
        page: 1,
        pageSize: 20,
        total: 0
      }
    };
  },
  mounted() {
    this.setDefaultTimeRange();
    this.fetchAdvertisers();
  },
  methods: {
    setDefaultTimeRange() {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      
      this.queryParams.endTime = this.formatDate(end);
      this.queryParams.startTime = this.formatDate(start);
    },
    
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    
    async handleQuery() {
      this.loading = true;
      
      // 重置分页到第一页
      if (this.activeTab === 'efficiency') {
        this.efficiencyPagination.page = 1;
      }
      
      try {
        // 根据当前 Tab 调用不同接口
        if (this.activeTab === 'efficiency') {
          await this.fetchEfficiencyAnalysis();
        } else if (this.activeTab === 'topRoas') {
          await this.fetchTopRoas();
        } else if (this.activeTab === 'heatmap') {
          await this.fetchHeatmap();
        }
      } catch (error) {
        console.error('查询失败:', error);
        this.$message.error('查询失败: ' + (error.message || '网络错误'));
      } finally {
        this.loading = false;
      }
    },
    
    handleTabClick(tab) {
      // Tab 切换时自动查询
      if (tab.name === 'efficiency' && !this.efficiencyData) {
        this.handleQuery();
      } else if (tab.name === 'topRoas' && !this.topRoasData) {
        this.handleQuery();
      } else if (tab.name === 'heatmap' && !this.heatmapData) {
        this.handleQuery();
      }
    },
    
    handleReset() {
      this.queryParams = {
        advertiserId: '',
        startTime: '',
        endTime: ''
      };
      this.setDefaultTimeRange();
      this.efficiencyData = null;
      this.efficiencyPagination = {
        page: 1,
        pageSize: 50,
        total: 0
      };
      this.topRoasData = null;
      this.heatmapData = null;
      this.trendData = null;
    },
    
    // 回收效率分析 - 分页大小变化
    handleEfficiencySizeChange(val) {
      this.efficiencyPagination.pageSize = val;
      this.efficiencyPagination.page = 1;
      this.fetchEfficiencyAnalysis();
    },
    
    // 回收效率分析 - 页码变化
    handleEfficiencyCurrentChange(val) {
      this.efficiencyPagination.page = val;
      this.fetchEfficiencyAnalysis();
    },
    
    // 获取回收效率分析
    async fetchEfficiencyAnalysis() {
      const params = {
        startTime: this.queryParams.startTime + ' 00:00:00',
        endTime: this.queryParams.endTime + ' 23:59:59',
        page: this.efficiencyPagination.page,
        pageSize: this.efficiencyPagination.pageSize
      };
      
      if (this.queryParams.advertiserId) {
        params.advertiserId = this.queryParams.advertiserId;
      }
      
      console.log('查询参数:', params); // 调试日志
      
      const response = await this.$http.get('https://tiktoktf.darknight.games/api/api/v1/adgroup/hourly-revenue/efficiency-analysis', {
        withCredentials: false,
        params
      });
      
      console.log('返回数据:', response.data); // 调试日志
      
      if (response.data.code === 200) {
        this.efficiencyData = response.data.data;
        this.efficiencyPagination.total = response.data.data.total || 0;
        this.efficiencyPagination.page = response.data.data.page || 1;
        this.efficiencyPagination.pageSize = response.data.data.pageSize || 100;
        this.$message.success(`查询成功，共 ${this.efficiencyPagination.total} 条数据`);
      } else {
        this.$message.error(response.data.message || '查询失败');
      }
    },
    
    // 获取 Top ROI
    async fetchTopRoas() {
      const params = {
        startTime: this.queryParams.startTime + ' 00:00:00',
        endTime: this.queryParams.endTime + ' 23:59:59',
        topN: 10
      };
      
      if (this.queryParams.advertiserId) {
        params.advertiserId = this.queryParams.advertiserId;
      }
      
      const response = await this.$http.get('https://tiktoktf.darknight.games/api/api/v1/adgroup/hourly-revenue/top-roas', {
        withCredentials: false,
        params
      });
      
      if (response.data.code === 200) {
        this.topRoasData = response.data.data;
        this.$message.success('查询成功');
      } else {
        this.$message.error(response.data.message || '查询失败');
      }
    },
    
    // 获取热力图数据
    async fetchHeatmap() {
      const params = {
        startTime: this.queryParams.startTime + ' 00:00:00',
        endTime: this.queryParams.endTime + ' 23:59:59'
      };
      
      if (this.queryParams.advertiserId) {
        params.advertiserId = this.queryParams.advertiserId;
      }
      
      const response = await this.$http.get('https://tiktoktf.darknight.games/api/api/v1/adgroup/hourly-revenue/time-heatmap', {
        withCredentials: false,
        params
      });
      
      if (response.data.code === 200) {
        this.heatmapData = response.data.data;
        this.$message.success('查询成功');
        
        this.$nextTick(() => {
          this.initHeatmapChart();
        });
      } else {
        this.$message.error(response.data.message || '查询失败');
      }
    },
    
    // 初始化热力图
    initHeatmapChart() {
      if (!this.$refs.heatmapChart || !this.heatmapData) return;
      
      if (this.heatmapChart) {
        this.heatmapChart.dispose();
      }
      
      this.heatmapChart = echarts.init(this.$refs.heatmapChart);
      
      const hours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);
      const days = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
      
      // 构建热力图数据
      const data = this.heatmapData.heatmap_data.map(item => {
        return [
          item.day_of_week,
          item.hour_of_day,
          parseFloat(item.roi.toFixed(2))
        ];
      });
      
      const option = {
        title: {
          text: '一周7天×24小时 ROI 分布热力图',
          left: 'center'
        },
        tooltip: {
          position: 'top',
          formatter: function(params) {
            return `${days[params.value[0]]} ${hours[params.value[1]]}<br/>ROI: ${params.value[2]}`;
          }
        },
        grid: {
          height: '70%',
          top: '15%'
        },
        xAxis: {
          type: 'category',
          data: days,
          splitArea: {
            show: true
          }
        },
        yAxis: {
          type: 'category',
          data: hours,
          splitArea: {
            show: true
          }
        },
        visualMap: {
          min: 0,
          max: 3,
          calculable: true,
          orient: 'horizontal',
          left: 'center',
          bottom: '5%',
          inRange: {
            color: ['#F56C6C', '#E6A23C', '#67C23A', '#409EFF']
          },
          formatter: function(value) {
            return value.toFixed(2);
          }
        },
        series: [{
          name: 'ROI',
          type: 'heatmap',
          data: data,
          label: {
            show: true,
            formatter: function(params) {
              return params.value[2];
            }
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }]
      };
      
      this.heatmapChart.setOption(option);
    },
    
    // 24小时趋势查询
    async handleTrendQuery() {
      if (!this.trendQuery.adgroupId) {
        this.$message.warning('请输入广告组ID');
        return;
      }
      
      if (!this.trendQuery.date) {
        this.$message.warning('请选择日期');
        return;
      }
      
      this.loading = true;
      
      try {
        const params = {
          advertiserId: this.queryParams.advertiserId || '',
          adgroupId: this.trendQuery.adgroupId,
          date: this.trendQuery.date
        };
        
        const response = await this.$http.get('https://tiktoktf.darknight.games/api/api/v1/adgroup/hourly-revenue/hourly-trend', {
          withCredentials: false,
          params
        });
        
        if (response.data.code === 200) {
          this.trendData = response.data.data;
          this.$message.success('查询成功');
          
          this.$nextTick(() => {
            this.initTrendChart();
          });
        } else {
          this.$message.error(response.data.message || '查询失败');
        }
      } catch (error) {
        console.error('查询失败:', error);
        this.$message.error('查询失败: ' + (error.message || '网络错误'));
      } finally {
        this.loading = false;
      }
    },
    
    // 初始化趋势图
    initTrendChart() {
      if (!this.$refs.trendChart || !this.trendData) return;
      
      if (this.trendChart) {
        this.trendChart.dispose();
      }
      
      this.trendChart = echarts.init(this.$refs.trendChart);
      
      const hours = this.trendData.trend_data.map(item => `${item.hour_of_day}:00`);
      const spendData = this.trendData.trend_data.map(item => item.spend);
      const revenueData = this.trendData.trend_data.map(item => item.revenue);
      const roiData = this.trendData.trend_data.map(item => item.roas);
      
      const option = {
        title: {
          text: '24小时消耗与收入趋势',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        legend: {
          data: ['消耗', '收入', 'ROI'],
          top: 30
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: 80,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: hours,
          boundaryGap: false
        },
        yAxis: [
          {
            type: 'value',
            name: '金额',
            axisLabel: {
              formatter: '${value}'
            }
          },
          {
            type: 'value',
            name: 'ROI',
            position: 'right'
          }
        ],
        series: [
          {
            name: '消耗',
            type: 'bar',
            data: spendData,
            itemStyle: {
              color: '#409EFF'
            }
          },
          {
            name: '收入',
            type: 'line',
            data: revenueData,
            smooth: true,
            itemStyle: {
              color: '#67C23A'
            },
            areaStyle: {
              color: 'rgba(103, 194, 58, 0.2)'
            }
          },
          {
            name: 'ROI',
            type: 'line',
            yAxisIndex: 1,
            data: roiData,
            smooth: true,
            itemStyle: {
              color: '#E6A23C'
            }
          }
        ]
      };
      
      this.trendChart.setOption(option);
    },
    
    // 24小时收入矩阵查询
    async handleMatrixQuery() {
      if (!this.matrixQuery.date) {
        this.$message.warning('请选择日期');
        return;
      }
      
      // 重置分页到第一页
      this.matrixPagination.page = 1;
      
      this.loading = true;
      
      try {
        const params = {
          date: this.matrixQuery.date,
          page: this.matrixPagination.page,
          pageSize: this.matrixPagination.pageSize
        };
        
        if (this.queryParams.advertiserId) {
          params.advertiserId = this.queryParams.advertiserId;
        }
        
        const response = await this.$http.get('https://tiktoktf.darknight.games/api/api/v1/adgroup/hourly-revenue/daily-matrix', {
          withCredentials: false,
          params
        });
        
        if (response.data.code === 200) {
          this.matrixData = response.data.data;
          this.matrixPagination.total = response.data.data.total || 0;
          this.matrixPagination.page = response.data.data.page || 1;
          this.matrixPagination.pageSize = response.data.data.pageSize || 20;
          this.$message.success(`查询成功，共 ${this.matrixPagination.total} 个广告组`);
        } else {
          this.$message.error(response.data.message || '查询失败');
        }
      } catch (error) {
        console.error('查询失败:', error);
        this.$message.error('查询失败: ' + (error.message || '网络错误'));
      } finally {
        this.loading = false;
      }
    },
    
    // 24小时矩阵 - 分页大小变化
    handleMatrixSizeChange(val) {
      this.matrixPagination.pageSize = val;
      this.matrixPagination.page = 1;
      this.handleMatrixQuery();
    },
    
    // 24小时矩阵 - 页码变化
    handleMatrixCurrentChange(val) {
      this.matrixPagination.page = val;
      this.handleMatrixQuery();
    },
    
    // 获取广告主列表
    async fetchAdvertisers() {
      try {
        const res = await advertiserApi.getList({
          page: 1,
          pageSize: 100
        });
        if (res.code === 0) {
          this.advertiserList = res.data.list || [];
        }
      } catch (error) {
        console.error('获取广告主列表失败', error);
      }
    },
    
    formatMoney(value) {
      if (!value && value !== 0) return '$0.00';
      return `$${Number(value).toFixed(2)}`;
    },
    
    getRoiClass(roi) {
      if (!roi) return '';
      if (roi >= 1.5) return 'roi-excellent';
      if (roi >= 1.0) return 'roi-good';
      if (roi >= 0.5) return 'roi-warning';
      return 'roi-danger';
    },
    
    getProgressColor(hour) {
      if (hour >= 24) return '#67C23A';
      if (hour >= 12) return '#409EFF';
      if (hour >= 6) return '#E6A23C';
      return '#F56C6C';
    }
  },
  beforeDestroy() {
    if (this.heatmapChart) {
      this.heatmapChart.dispose();
    }
    if (this.trendChart) {
      this.trendChart.dispose();
    }
  }
};
</script>

<style scoped>
.revenue-analysis-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 60px);
}

.query-card {
  margin-bottom: 20px;
  border-radius: 4px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.card-header i {
  font-size: 16px;
  color: #409EFF;
}

.analysis-content {
  padding: 10px;
}

/* 汇总卡片 */
.summary-cards {
  margin-bottom: 20px;
}

.summary-card {
  display: flex;
  align-items: center;
  padding: 10px;
}

.card-icon {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.card-icon i {
  font-size: 24px;
  color: white;
}

.card-info {
  flex: 1;
}

.card-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 5px;
}

.card-value {
  font-size: 20px;
  font-weight: bold;
  color: #303133;
}

/* ROI 样式 */
.roi-excellent { color: #67C23A; }
.roi-good { color: #409EFF; }
.roi-warning { color: #E6A23C; }
.roi-danger { color: #F56C6C; }

/* 金额样式 */
.amount {
  font-size: 13px;
  color: #303133;
}

.amount.success { color: #67C23A; }

/* 表格样式 - 简约风格 */
::v-deep .el-table {
  font-size: 13px;
  color: #606266;
  border: none;
}

::v-deep .el-table th {
  font-size: 13px;
  font-weight: 500;
  color: #909399;
  background: #fafafa;
  border-bottom: 1px solid #ebeef5;
  padding: 12px 0;
}

::v-deep .el-table td {
  font-size: 13px;
  padding: 10px 0;
  border-bottom: 1px solid #ebeef5;
}

::v-deep .el-table--enable-row-hover .el-table__body tr:hover > td {
  background-color: #f5f7fa;
}

::v-deep .el-table__row {
  background-color: #fff;
}

::v-deep .el-table__row.current-row > td {
  background-color: #ecf5ff;
}

/* 按钮样式 */
::v-deep .el-button--text {
  font-size: 13px;
  padding: 0;
  color: #409EFF;
}

::v-deep .el-button--text:hover {
  color: #66b1ff;
}

/* 洞察卡片 */
.insight-card {
  margin-bottom: 20px;
}

.insight-card.best ::v-deep .el-card__header {
  background: #f0f9ff;
  border-bottom: 2px solid #67C23A;
}

.insight-card.worst ::v-deep .el-card__header {
  background: #fef0f0;
  border-bottom: 2px solid #F56C6C;
}

.insight-time {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 10px;
}

.insight-roi {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 10px;
}

.insight-detail {
  font-size: 12px;
  color: #909399;
}

/* 热力图汇总 */
.heatmap-summary {
  margin-bottom: 20px;
}

/* 24小时矩阵单元格样式 */
.hour-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  line-height: 1.5;
  font-size: 11px;
  padding: 4px 0;
  text-align: center;
}

.hour-spend {
  color: #787a80;
  font-size: 11px;
  min-width: 50px;
  text-align: right;
}

.hour-roas {
  font-weight: 500;
  font-size: 11px;
  min-width: 40px;
  text-align: left;
}

.hour-roas.roi-excellent { color: #85ce61; }
.hour-roas.roi-good { color: #66b1ff; }
.hour-roas.roi-warning { color: #e6a23c; }
.hour-roas.roi-danger { color: #f78989; }
</style>
